import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert, Stack } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DatabaseIcon from '@mui/icons-material/Storage';
import CombinedExportIcon from '@mui/icons-material/ImportExport';
import axios from '../../utils/axiosConfig';

import logger from '../../utils/logger';

interface ExportCompetitionCardsButtonProps {
  competitionId: string;
  competitionName?: string;
}

type ExportType = 'json' | 'db' | 'both';

const ExportCompetitionCardsButton: React.FC<ExportCompetitionCardsButtonProps> = ({ 
  competitionId, 
  competitionName 
}) => {
  const [loading, setLoading] = useState<ExportType | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const handleExport = async (exportType: ExportType) => {
    setLoading(exportType);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      switch (exportType) {
        case 'json':
          // Default behavior - just export JSON
          break;
        case 'db':
          // Save to DB only, no JSON return
          params.append('saveToDb', 'true');
          params.append('returnJson', 'false');
          break;
        case 'both':
          // Save to DB and return JSON for download
          params.append('saveToDb', 'true');
          params.append('returnJson', 'true');
          break;
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`/api/admin/competitioncards/export/${competitionId}${queryString}`);
      
      if (exportType === 'json' || exportType === 'both') {
        // Create a JSON file for download if we're returning JSON
        const exportData = response.data;
        
        // Create a JSON file for download
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        // Generate filename with competition name and date
        const date = new Date().toISOString().split('T')[0];
        const safeName = exportData.competition.name
          .replace(/[^a-z0-9]/gi, '-')
          .toLowerCase()
          .replace(/-+/g, '-');
        const filename = `${safeName}-cards-${date}.json`;
        
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        setSuccessMessage(`${exportData.count} Wettkampfkarten erfolgreich ${exportType === 'both' ? 'exportiert und in Datenbank gespeichert' : 'exportiert'}`);
      } else {
        // For DB-only export, use the message from the server
        setSuccessMessage(response.data.message || 'Wettkampfkarten erfolgreich in Datenbank gespeichert');
      }
      
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Export. Bitte versuchen Sie es erneut.');
      //console.error('Export error:', err);
      logger.error('Export error:', err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <Stack spacing={1} direction="column" alignItems="flex-start">
        <Button
          variant="contained"
          color="primary"
          startIcon={loading === 'json' ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          onClick={() => handleExport('json')}
          disabled={loading !== null}
          size="small"
          fullWidth
        >
          {loading === 'json' ? 'Exportiere...' : 'Karten exportieren (JSON)'}
        </Button>
        
        <Button
          variant="contained"
          color="secondary"
          startIcon={loading === 'db' ? <CircularProgress size={20} color="inherit" /> : <DatabaseIcon />}
          onClick={() => handleExport('db')}
          disabled={loading !== null}
          size="small"
          fullWidth
        >
          {loading === 'db' ? 'Speichere in DB...' : 'Karten Export in DB'}
        </Button>
        
        <Button
          variant="contained"
          color="info"
          startIcon={loading === 'both' ? <CircularProgress size={20} color="inherit" /> : <CombinedExportIcon />}
          onClick={() => handleExport('both')}
          disabled={loading !== null}
          size="small"
          fullWidth
        >
          {loading === 'both' ? 'Exportiere...' : 'Karten Export JSON und DB'}
        </Button>
      </Stack>
      
      <Snackbar 
        open={success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportCompetitionCardsButton;